import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { AppStrings, formatNumber, logApiErrorToSentry } from "../../utils";
import { DebitCard, NEFT, NetBanking } from "../../../../assets/Icons";
import { styles } from "./styles";
import { NeftRtgsModal, SendPaymentLinkModal } from "../../components/modal";
import { remittanceActions } from "../../state/remittanceSlice";
import { remittanceApi } from "../../api/remittanceApi";
import MixpanelConfig from "../../../../constants/MixpanelConfig";

const RemittancePayment: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authState = useAppSelector((state) => state.auth);
  const remitterState = useAppSelector((state) => state.remittance);
  const { transactionDetail, payment, remitter } = useAppSelector((state) => state.remittance);

  {
    /* Initial API call and condition when user came to this page */
  }
  useEffect(() => {
    // dispatch(remittanceActions.fetchGetNeftRtgsBankDetailsRequested({
    //   ad1Transaction: transactionDetail?.data?.purposeCode === "S0305" ? false : true,
    //   referrerVendorCode: transactionDetail?.data?.referrerVendor
    // }));
    MixpanelConfig.trackEvent(MixpanelConfig.eventNames.New_Payment_Landing, {
      authState: authState,
      remitterState: remitterState,
      transactionDetail: transactionDetail,
    });

    dispatch(remittanceActions.fetchNetBankingListRequested());
  }, []);

  useEffect(() => {
    if (payment?.neftRtgsBankDetails?.error) {
      toast.error(payment?.neftRtgsBankDetails?.error);
    }
    if (payment?.netBankingList?.error) {
      toast.error(payment?.netBankingList?.error);
    }
  }, [payment?.neftRtgsBankDetails?.error, payment?.netBankingList?.error]);

  useEffect(() => {
    if (remitterState?.progresStep?.activeStepNumber < 6) {
      navigate("/send/calculator", { replace: true });
    }
  }, [remitterState?.progresStep?.activeStepNumber]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const sendPaymentLink = () => {
    const pennyDropPayload = {
      cusAcc: payment?.sendPaymentLinkInput?.accountNumber,
      ifscCode: payment?.sendPaymentLinkInput?.IFSCcode,
      isVpa: false,
      transactionID: transactionDetail?.data?.id_transaction,
      userId: 0,
      userKey: transactionDetail?.data?.remitterId,
      vpa: "",
    };
    const requestRazorLinkPayload = {
      accNum: payment?.sendPaymentLinkInput?.accountNumber,
      ifsc: payment?.sendPaymentLinkInput?.IFSCcode,
      paymentMode: "NET_BANKING",
      transactionID: transactionDetail?.data?.id_transaction,
      userId: 0,
      userKey: transactionDetail?.data?.remitterId,
    };
    dispatch(remittanceActions.setSendPaymentLinkInput({ pennyDropLoading: true }));
    remittanceApi
      .postPennyDrop(pennyDropPayload)
      .then((response: any) => {
        if (response?.data?.success) {
          dispatch(
            remittanceActions.setSendPaymentLinkInput({
              pennyDropLoading: false,
              requestRazorLoading: true,
            })
          );
          remittanceApi
            .postRequestRazorPayLink(requestRazorLinkPayload)
            .then((response: any) => {
              if (response?.data?.success) {
                toast.success("Link sent successfully to remitter's mobile number");
                dispatch(
                  remittanceActions.setSendPaymentLinkInput({
                    pennyDropLoading: false,
                    requestRazorLoading: false,
                    bankDropdown: null,
                    accountNumber: "",
                    IFSCcode: "",
                  })
                );
                dispatch(remittanceActions.setModalType(""));
                navigate("/send/confirmation");
              } else {
                dispatch(
                  remittanceActions.setSendPaymentLinkInput({
                    pennyDropLoading: false,
                    requestRazorLoading: false,
                  })
                );
                toast.error(AppStrings.GENERIC_ERROR);
              }
            })
            .catch((error: any) => {
              logApiErrorToSentry(error, "sendPaymentLink postRequestRazorPayLink");
              toast.error(
                error?.response?.data?.error?.type ||
                  error?.response?.error ||
                  error?.message ||
                  AppStrings.GENERIC_ERROR
              );
              dispatch(
                remittanceActions.setSendPaymentLinkInput({
                  pennyDropLoading: false,
                  requestRazorLoading: false,
                })
              );
            });
        } else {
          dispatch(
            remittanceActions.setSendPaymentLinkInput({
              pennyDropLoading: false,
              requestRazorLoading: false,
            })
          );
          toast.error(AppStrings.GENERIC_ERROR);
        }
      })
      .catch((error: any) => {
        logApiErrorToSentry(error, "sendPaymentLink postPennyDrop");
        toast.error(
          error?.response?.data?.error?.type ||
            error?.response?.error ||
            error?.message ||
            AppStrings.GENERIC_ERROR
        );
        dispatch(
          remittanceActions.setSendPaymentLinkInput({
            pennyDropLoading: false,
            requestRazorLoading: false,
          })
        );
      });
  };

  return (
    <>
      <div className={styles.container}>
        <ToastContainer autoClose={3000} position="top-right" />

        {/* Header */}
        <div id="header" className={styles.headerWrapper}>
          <span className={styles.transactionIdText}>
            {AppStrings.PAYMENT_SCREEN.TRANSACTION_ID_LABEL}{" "}
            {transactionDetail?.data?.transactionNumber}
          </span>

          <span className={styles.payableAmountText}>
            {AppStrings.PAYMENT_SCREEN.PAYABLE_AMOUNT_LABEL}{" "}
            {formatNumber(transactionDetail?.data?.total_pay)} {AppStrings.PAYMENT_SCREEN.CURRENCY}
          </span>

          <span className={styles.subtitleText}>
            {AppStrings.PAYMENT_SCREEN.SELECT_PAYMENT_METHOD}
          </span>
        </div>

        {/* Payment Options */}
        <div id="boxes" className={styles.paymentOptionsWrapper}>
          <div
            id="neft"
            onClick={() => {
              dispatch(remittanceActions.setModalType("NEFT_RTGS"));
              dispatch(
                remittanceActions.fetchGetNeftRtgsBankDetailsRequested({
                  ad1Transaction: transactionDetail?.data?.purposeCode === "S0305" ? false : true,
                  referrerVendorCode: transactionDetail?.data?.referrerVendor,
                })
              );
            }}
            className={styles.payButton}
          >
            <NEFT />
            <span className={styles.paymentLabelText}>{AppStrings.PAYMENT_SCREEN.NEFT_RTG}</span>
          </div>

          {/* Removed "Send Payment Link" option per product request */}
        </div>
      </div>
      <NeftRtgsModal
        visible={remitterState?.modalType === "NEFT_RTGS"}
        // onYes={() => {
        //   dispatch(remittanceActions.setModalType(""));
        //   dispatch(remittanceActions.setAmountCalculation({ isEducation: true }));
        // }}
        // onNo={() => {
        //   dispatch(remittanceActions.setModalType(""));
        //   dispatch(remittanceActions.setAmountCalculation({ isEducation: false }));
        // }}
      />
      <SendPaymentLinkModal
        visible={remitterState?.modalType === "SEND_PAYMENT_LINK"}
        onSubmit={sendPaymentLink}
        onClose={() => {
          dispatch(remittanceActions.setModalType(""));
        }}
      />
    </>
  );
};

export default RemittancePayment;
